import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { agentlessDetailsQuery, type AgentlessDetailsPage, type AgentlessDocument } from '../api/inventoree'
import { removeDocument } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from '../devices/screens'

/**
 * Fila Details a unui aparat fara agent - inventoree/agentless/details.php:
 * tabelul details-table, campurile custom (Yes/No pe tip 5), docsList.
 */

function Na({ value }: { value: string }) {
  return value !== '' ? <>{value}</> : <em>n/a</em>
}

function Documents({ id, initial }: { id: number; initial: AgentlessDocument[] }) {
  const [docs, setDocs] = useState(initial)
  const client = useQueryClient()

  const remove = async (doc: AgentlessDocument) => {
    if (!window.confirm('Are you sure?')) return
    try {
      await removeDocument(id, doc.id)
      setDocs((list) => list.filter((d) => d.id !== doc.id))
      void client.invalidateQueries({ queryKey: ['devices', id, 'agentless-details'] })
    } catch {
      /* details.php nu spune nimic la o scoatere picata: randul ramane. */
    }
  }

  return (
    <div className="row">
      <div className="col-md-12 divider">
        <h4 className="bordered">
          <strong>Device</strong> documents
        </h4>
      </div>
      <div className="col-md-12 ">
        <table className="table table-striped centered docsList">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <tr>
                <td colSpan={4}>No documents...</td>
              </tr>
            ) : (
              docs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <i className="fa fa-file-alt"></i> {d.name}
                  </td>
                  <td>{d.type}</td>
                  <td>{d.size}</td>
                  <td>{d.uploaded}</td>
                  <td>
                    {d.download ? (
                      <a href={d.download} className="btn btn-primary btn-xs">
                        <i className="fa fa-download"></i> Download
                      </a>
                    ) : null}{' '}
                    <button className="btn btn-danger btn-xs removeDoc" data-doc={d.id} onClick={() => void remove(d)}>
                      <i className="fas fa-times-circle"></i> Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Loaded({ page }: { page: AgentlessDetailsPage }) {
  return (
    <>
      <div className="row">
        <div className="col-md-8">
          <table style={{ width: '100%' }} className="details-table">
            <tbody>
              <tr>
                <td colSpan={2}>
                  <div className="row">
                    <div className="col-md-12 divider" style={{ marginTop: 0 }}>
                      <h4 className="bordered">
                        <strong>Device</strong> details
                      </h4>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="title">Name:</td>
                <td>
                  <Na value={page.name} />
                </td>
              </tr>
              <tr>
                <td className="title">Type:</td>
                <td>
                  <Na value={page.type} />
                </td>
              </tr>
              <tr>
                <td className="title">Brand:</td>
                <td>
                  <Na value={page.brand} />
                </td>
              </tr>
              <tr>
                <td className="title">Model:</td>
                <td>
                  <Na value={page.model} />
                </td>
              </tr>
              <tr>
                <td className="title">Serial:</td>
                <td>
                  <Na value={page.serial} />
                </td>
              </tr>
              <tr>
                <td className="title">Acquisition date:</td>
                <td>
                  <i className="far fa-calendar-check"></i> <Na value={page.acquisition} />
                </td>
              </tr>
              <tr>
                <td className="title">Warranty expiry date:</td>
                <td>
                  <i className="far fa-calendar-times"></i> <Na value={page.warranty} />
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #ccc' }}>
                <td className="title">Classified info:</td>
                <td>
                  <Na value={page.classified} />
                </td>
              </tr>
              <tr>
                <td className="title">Owner:</td>
                <td>
                  <i className="fa fa-user"></i> {page.ownerId > 0 ? page.owner : <em>n/a</em>}
                </td>
              </tr>
              <tr>
                <td className="title">Office:</td>
                <td>
                  <i className="fa fa-map-marker"></i> {page.office !== '' ? page.office : <em>n/a</em>}
                </td>
              </tr>
              <tr>
                <td className="title">Department:</td>
                <td>
                  <Na value={page.department} />
                </td>
              </tr>
              <tr>
                <td className="title">Group:</td>
                <td>
                  <i className="fa fa-users"></i> <Na value={page.group} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-md-4">
          <table style={{ width: '100%' }} className="details-table">
            <tbody>
              <tr>
                <td colSpan={2}>
                  <div className="row">
                    <div className="col-md-12 divider" style={{ marginTop: 0 }}>
                      <h4 className="bordered">
                        <strong>Custom</strong> fields
                      </h4>
                    </div>
                  </div>
                </td>
              </tr>
              {page.customFields.length === 0 ? (
                <tr>
                  <td colSpan={2}>
                    <span className="label label-default">No custom fields.</span>
                  </td>
                </tr>
              ) : (
                page.customFields.map((f) => (
                  <tr key={f.label}>
                    <td className="title">{f.label}:</td>
                    <td>{f.value}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Documents id={page.id} initial={page.documents} />
    </>
  )
}

export function AgentlessDetails({ id }: ScreenProps) {
  const page = useQuery(agentlessDetailsQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <Loaded page={page.data} />
}
